import classnames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import { Forex } from 'components/shared/UnderlyingInfo/Tooltip/Infos/Forex/Forex';
import { useSPIntl } from 'hooks/useIntl';
import { asEquityUnderlying, type Underlying } from 'models/underlying';
import type { ForexUnderlying } from 'models/underlying/forex';
import { ComponentProps } from 'react';
import { useSelector } from 'react-redux';
import { getIsUserInternal } from 'state/selectors';
import {
  isCreditUnderlying,
  isEquityUnderlying,
  isForexUnderlying,
  isRateUnderlying,
} from 'utils/underlying/typeGuards';

import { CurrencyFlagIcon } from '../../WithCountryFlag';
import { Credit } from './Infos/Credit/Credit';
import { Equity } from './Infos/Equity/Equity';
import { Rate } from './Infos/Rate/Rate';

import './style.scss';

export type UnderlyingTooltipProps = {
  underlying: Underlying;
  hasCutOff?: boolean;
  hasLegacyEsgEligibilityIcon?: boolean;
} & Pick<ComponentProps<'div'>, 'onMouseEnter' | 'onMouseLeave'>;

export function UnderlyingTooltip({ underlying, hasCutOff, ...divProps }: UnderlyingTooltipProps) {
  const { formatDisplayedDate } = useSPIntl();
  const isUserInternal = useSelector(getIsUserInternal);

  const displayCryptoExposed = asEquityUnderlying(underlying)?.cryptoExposed;

  return (
    <div className="underlying-tooltip text-secondary bg-primary-alt border p-3" {...divProps} role="tooltip">
      <p className="d-flex align-items-center underlying-tooltip__title border-bottom justify-content-between">
        <strong>{underlying.name}</strong>
      </p>
      <dl className="underlying-tooltip__info-list">
        {underlying.type && (
          <>
            <dt>
              <FormattedMessage id="underlying.info.type" />:
            </dt>
            <dd>
              <FormattedMessage id={`underlying.info.type.${underlying.type}` as any} />
            </dd>
          </>
        )}

        {underlying.currency && (
          <>
            <dt>
              <FormattedMessage id="underlying.info.currency" />:
            </dt>
            <dd>
              <CurrencyFlagIcon size="sm" currency={underlying.currency} className="me-1" />
              {underlying.currency}
            </dd>
          </>
        )}

        {isCreditUnderlying(underlying) && <Credit {...underlying} />}
        {isRateUnderlying(underlying) && <Rate {...underlying} />}
        {isEquityUnderlying(underlying) && <Equity {...underlying} />}
        {isForexUnderlying(underlying) && <Forex hasCutOff={hasCutOff} underlying={underlying as ForexUnderlying} />}

        {isUserInternal && (
          <>
            <dt>
              <FormattedMessage id="underlying.info.restricted" />:
            </dt>
            <dd data-e2e="isUnderlyingRestricted">
              <span
                className={classnames({
                  'badge rounded-pill badge-warning': underlying.isRestricted,
                })}
              >
                <FormattedMessage id={underlying.isRestricted ? 'boolean.yes' : 'boolean.no'} />
              </span>
            </dd>

            {displayCryptoExposed && (
              <>
                <dt>
                  <FormattedMessage id="underlying.info.CryptoExposed" />:
                </dt>
                <dd>
                  <span
                    className={classnames({
                      'badge rounded-pill badge-success': displayCryptoExposed,
                    })}
                  >
                    <FormattedMessage id={displayCryptoExposed ? 'boolean.yes' : 'boolean.no'} />
                  </span>
                </dd>
              </>
            )}
          </>
        )}
        {underlying.expiryDate && (
          <>
            <dt>
              <FormattedMessage id="underlying.info.expiryDate" />:
            </dt>
            <dd data-e2e="underlying-expiryDate">{formatDisplayedDate(underlying.expiryDate)}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
